import { organizationsApi } from "api";
import { useCallback, useContext, useEffect, useRef } from "react";
import {
  Organization,
} from "model/organizationModel";
import { OrganizationsContext } from "contexts";
import useUser from "./useUser";

let inFlightUserId: number | null = null;

const useOrganizations = (
  sleep?: number,
): [Organization[], () => Promise<void>] => {
  const {
    state: { organizationsUserId, organizations },
    actions: { setOrganizations },
  } = useContext(OrganizationsContext);
  const user = useUser();
  const userId = user?.userId;
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const getOrganizations = useCallback(async (force = false) => {
    const activeUserId = user?.userId;
    if (!activeUserId) {
      inFlightUserId = null;
      setOrganizations([]);
      return;
    }

    if (!force && inFlightUserId === activeUserId) {
      return;
    }

    inFlightUserId = activeUserId;
    try {
      const { data } = await organizationsApi.get<{
        organizations: Organization[];
      }>(`user`);
      if (!isMountedRef.current || user?.userId !== activeUserId) {
        return;
      }
      setOrganizations(data.organizations);
    } catch {
      if (isMountedRef.current && user?.userId === activeUserId) {
        setOrganizations([]);
      }
    } finally {
      if (inFlightUserId === activeUserId) {
        inFlightUserId = null;
      }
    }
  }, [setOrganizations, user]);

  useEffect(() => {
    if (!userId) {
      inFlightUserId = null;
      if (organizations.length > 0 || organizationsUserId !== null) {
        setOrganizations([]);
      }
      return;
    }

    if (organizationsUserId === userId) {
      return;
    }

    const start = () => {
      getOrganizations();
    };

    if (sleep) {
      const timeoutId = setTimeout(start, sleep);
      return () => clearTimeout(timeoutId);
    }

    start();
  }, [
    userId,
    organizationsUserId,
    sleep,
    getOrganizations,
    setOrganizations,
    organizations.length,
  ]);

  return [organizations, () => getOrganizations(true)];
};

export default useOrganizations;
